/**
 * PaymentStatusBadge Component
 *
 * Displays payment status with appropriate styling and icons.
 * Ported from IPFiling/Payment (wizard removal) with multi-currency display support.
 */
import React from 'react';
import { CheckCircle, Clock, XCircle, AlertCircle, RotateCcw } from 'lucide-react';
import { Text } from '../../../common/Typography';

const ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW'];

const PaymentStatusBadge = ({ paymentStatus, status, amount, currency = 'USD', paymentDate, refundAmount, className = '' }) => {
  const getPaymentStatusInfo = () => {
    // Check for refund statuses first
    if (status === 'refunded' || paymentStatus === 'refunded') {
      return {
        icon: RotateCcw,
        text: 'Refund Issued',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        iconColor: 'text-orange-600'
      };
    }

    if (status === 'partially_refunded' || paymentStatus === 'partially_refunded') {
      return {
        icon: RotateCcw,
        text: 'Partial Refund Issued',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        iconColor: 'text-orange-600'
      };
    }

    // Check for completed payment using both paymentStatus and overall status
    const isPaymentCompleted = paymentStatus === 'paid' || status === 'payment_completed';

    if (isPaymentCompleted) {
      return {
        icon: CheckCircle,
        text: 'Payment Completed',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        iconColor: 'text-green-600'
      };
    }

    if (paymentStatus === 'pending') {
      return {
        icon: Clock,
        text: 'Payment Pending',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-600'
      };
    }

    if (paymentStatus === 'failed') {
      return {
        icon: XCircle,
        text: 'Payment Failed',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        iconColor: 'text-red-600'
      };
    }

    // Default: No payment yet
    return {
      icon: AlertCircle,
      text: 'Payment Required',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      iconColor: 'text-gray-600'
    };
  };

  const statusInfo = getPaymentStatusInfo();
  const StatusIcon = statusInfo.icon;

  const formatCurrency = (minorUnits) => {
    if (!minorUnits) return '';
    const code = (currency || 'USD').toUpperCase();
    const divisor = ZERO_DECIMAL_CURRENCIES.includes(code) ? 1 : 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: code,
      }).format(minorUnits / divisor);
    } catch (e) {
      return `${(minorUnits / divisor).toFixed(2)} ${code}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={`inline-flex items-center px-3 py-2 rounded-lg ${statusInfo.bgColor} ${className}`}>
      <StatusIcon className={`h-4 w-4 mr-2 ${statusInfo.iconColor}`} />
      <div className="flex flex-col">
        <Text className={`text-sm font-medium ${statusInfo.textColor}`}>
          {statusInfo.text}
        </Text>
        {amount && (
          <Text className={`text-xs ${statusInfo.textColor} opacity-75`}>
            {refundAmount && (status === 'refunded' || status === 'partially_refunded')
              ? `${formatCurrency(refundAmount)} refunded of ${formatCurrency(amount)}`
              : formatCurrency(amount)
            }
            {paymentDate && ` • ${formatDate(paymentDate)}`}
          </Text>
        )}
      </div>
    </div>
  );
};

export default PaymentStatusBadge;
